import { useDispatch, useSelector } from "react-redux";
import { context, isSpanContextValid, trace, type Span } from "@opentelemetry/api";
import { usePostHog } from "posthog-js/react";
import {
  applicationSelector,
  submitApplication,
  submitQuote,
  saveValue,
  getCurrentProductLenderId,
  setSubmissionModalOpen,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import type { ApplicationFormSlice, ApplicationState } from "@/store/slices/types/applicationFormSlice";
import {
  setLodgeAISession,
  openLodgeAIModal,
  setLodgeAIError,
  setLodgeAIQueue,
  resetLodgeAI,
} from "@/store/slices/lodgeAISlice";
import { buildBrokerLoopDeepLink } from "@/utils/brokerLoopDeepLink";
import { LodgeAIModalType } from "@/store/slices/types/lodgeAITypes";
// @ts-expect-error: ignoring as it is existing js file
import { updateLenderName } from "@/components/Utils/helperFunction";
import { findKeyValueInData, getBrokerLoopSessionLender } from "@/utils/lenderUtils";
import { useApplicationRouteType } from "./useApplicationRouteType";

interface LodgeAIType {
  name: string;
  position?: number;
  expiresAt?: number;
  enqueuedAt?: number;
}

const tracer = trace.getTracer("submission");

/**
 * The desktop app continues the submission trace from the deep link `traceId`, which accepts a W3C traceparent. It is
 * always marked sampled so the app records its spans even when the browser sampled this trace out.
 */
const getDeepLinkTraceParent = (span: Span) => {
  const spanContext = span.spanContext();
  return isSpanContextValid(spanContext) ? `00-${spanContext.traceId}-${spanContext.spanId}-01` : undefined;
};

const useSubmission = () => {
  const posthog = usePostHog();
  const applicationRouteType = useApplicationRouteType();
  const { _id, submissionModal, user, applicationType } = useSelector<ApplicationFormSlice, ApplicationState>(
    applicationSelector,
  );
  const dispatch = useDispatch();

  const handleSubmit = (isLodgeAI?: boolean, lodgeAISubmissionMethod?: "LOCAL") => {
    // A click handler has no active span, so start one that parents the submit request and the desktop app's trace.
    const submitSpan = tracer.startSpan("submission.submit", {
      attributes: {
        "application.id": _id,
        lender: submissionModal.lender ?? "",
        "submission.type": submissionModal.submissionType ?? "",
        "submission.lodgeai": Boolean(isLodgeAI),
      },
    });
    const activeTraceId = getDeepLinkTraceParent(submitSpan);
    const submitThunk = applicationRouteType === "quote" ? submitQuote : submitApplication;
    const baseSubmissionPayload = {
      applicationId: _id,
      lenderType: updateLenderName(submissionModal.lender ?? ""),
      productDetails: submissionModal.productDetails,
      submitType: submissionModal.submissionType,
    };
    const submissionPayload =
      applicationRouteType === "quote"
        ? baseSubmissionPayload
        : {
            ...baseSubmissionPayload,
            documents: submissionModal.selectedDocuments,
            browserType: lodgeAISubmissionMethod ?? "LOCAL",
          };

    context
      .with(trace.setSpan(context.active(), submitSpan), () => dispatch(submitThunk(submissionPayload)))
      // @ts-expect-error: existing dispatch
      .then((res) => {
        // If the lender is a LodgeAI lender, open the LodgeAI modal.
        // and close the current submission modal.
        if (isLodgeAI) {
          const sessionId = findKeyValueInData(res.payload.data?.data, "sessionId");
          const jwt = findKeyValueInData(res.payload.data?.data, "jwt");
          const type = findKeyValueInData(res.payload.data?.data, "type") as unknown as LodgeAIType;
          const lender = getBrokerLoopSessionLender(res.payload.data?.data) ?? submissionModal.lender;
          submitSpan.setAttribute("lodgeai.deep_link.lender", lender ?? "");
          if (!!sessionId && !!jwt) {
            if (lodgeAISubmissionMethod === "LOCAL") {
              if (type?.name === "QUEUED") {
                dispatch(
                  setLodgeAISession({
                    sessionId,
                    jwt,
                    isActive: true,
                    expiresAt: type?.expiresAt,
                    lender,
                    traceId: activeTraceId,
                  }),
                );
                dispatch(setLodgeAIQueue({ inProgress: true, queuePosition: type?.position ?? 0 }));
                dispatch(openLodgeAIModal({ modalType: LodgeAIModalType.Queue }));
              } else {
                const deepLinkParams = new URLSearchParams({
                  sessionId: sessionId ?? "",
                  token: jwt ?? "",
                  LENDER: lender ?? "",
                });
                if (activeTraceId) {
                  deepLinkParams.set("traceId", activeTraceId);
                }
                const deepLink = buildBrokerLoopDeepLink(deepLinkParams);
                if (typeof window !== "undefined") {
                  window.open(deepLink, "_blank");
                }
                dispatch(resetLodgeAI());
                dispatch(setSubmissionModalOpen({ isOpen: false }));
                return;
              }
            }
            dispatch(setSubmissionModalOpen({ isOpen: false }));
          } else {
            const isErrorArray = Array.isArray(res.payload?.data?.error);
            const isValidationError = isErrorArray && res.payload?.data?.error[0]?.code === "VALIDATION_ERROR";
            if (!isValidationError) {
              const errorMessage = isErrorArray ? res.payload?.data?.error[0]?.detail : res.payload?.data?.error;
              dispatch(
                setLodgeAIError({
                  title: "Failed to create session",
                  message: errorMessage ?? res.payload?.status_message ?? "Unknown error",
                }),
              );
            }
          }
        } else {
          dispatch(saveValue({ applyApplication: true }));
          dispatch(getCurrentProductLenderId(""));
        }
      })
      .finally(() => {
        submitSpan.end();
        posthog?.capture("submit_application_clicked", {
          submitType: submissionModal.submissionType,
          applicationId: _id,
          lender: submissionModal.lender,
          browserType: lodgeAISubmissionMethod,
          organisation: user?.organisation,
          applicationType,
        });
      });
  };

  return { handleSubmit };
};

export { useSubmission };
